import { inject, Injectable } from '@angular/core';
import { Category, ICategoryDataV2 } from '@df/catalog/category/category';
import { ICategoryDataV3, Model } from '@df/catalog/model';
import { IProductData, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketService } from '@df/session/api/basket/basket.service';
import { Customer } from '@df/session/api/customer';
import { CustomerModel } from '@df/session/api/customer.model';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { bufferPromise } from '@df/util/fn/buffer-promise.fn';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { Subscription } from 'rxjs';
import { headerComponentId } from '../../layout/page/header/page-header.component';
import { IPageHeaderBlockContent } from '../../layout/page/header/page-header.interface';
import { NProductVipContentModal } from './vip-content-modal/vip-content-modal.component';
import { NVipRestrictedContentModal } from './vip-restricted-modal/vip-restricted-modal.component';
import { CMS_CONFIG_VIP } from './vip.cms-config';

declare module '@df/catalog/model' {
    interface ICategoryDataV3 {
        vip_only?: boolean;
    }
}

declare module '@df/catalog/product/product' {
    interface IProductDataV1 {
        lm_subcategory?: number[];
        season?: number[];
    }
}
declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            allowed_subcategory?: number[];
            allowed_seasons?: number[];
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class VipService {
    seasonalLoaded = false;

    private subscriptions: Subscription = new Subscription();
    private vipContentModalRef: ModalRef<any> | undefined;
    private restrictedContentRef: ModalRef<any> | undefined;
    private vipIds: number[] | undefined;
    private iCmsBlockResource = inject(ICmsBlockResource);

    private restrictedData = new Map<Product, IProductData>();
    private seasonalyBlockedProductsIds = new Set<number>();
    private whitelistedGroups = new Set<number>();

    constructor(
        private dfModalService: DfModalService,
        private customer: Customer,
        private cmsConfigResource: ICmsConfigResource,
        private customerModel: CustomerModel,
        private app: App,
        private meta: Meta,
        private client: Client,
        private basketService: BasketService,
        private productResource: ProductResource,
        private timeoutService: TimeoutService
    ) {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        this.subscriptions.add(
            this.meta.subscribe(() => {
                this.recheck();
            })
        );
        this.subscriptions.add(
            this.customerModel.obs.data.subscribe(() => {
                const model = this.meta.model;
                const isRestricted = this.restrictedData.has(model as Product);
                this.applyProductsRestriction(this.productResource.getAll());
                if (isRestricted && !this.restrictedData.has(model as Product)) {
                    window.location.reload();
                }
                this.recheck();
            })
        );
        this.subscriptions.add(
            this.client.obs.resize.subscribe(() => {
                this.recheck();
            })
        );
        this.subscriptions.add(this.customer.basket.subscribe(() => this.sanitizeBasket()));
        this.loadSeasonalData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    async loadForCategory(category: Category): Promise<void> {
        if (this.isVipCategory(category)) {
            await this.load();
        }
        await this.loadForProducts(category.products);
    }

    async loadForProducts(products: Product[]): Promise<any> {
        const restrictedModels = products.filter(product => product.data && this.isRestrictedProduct(product.data));
        if (restrictedModels.length) {
            if (!this.customer.model.loaded) {
                await this.customer.model.load();
            }
            this.applyProductsRestriction(restrictedModels);
        }
    }

    async loadAndCheckProduct(product: Product): Promise<boolean> {
        await this.loadForProducts([product]);
        if (this.isRestrictionApplied(product)) {
            return true;
        }
        return false;
    }

    isRestrictionApplied(product: Product): boolean {
        return this.restrictedData.has(product);
    }

    async loadSeasonalData() {
        return bufferPromise('seasonal-products', async () => {
            const block = await this.iCmsBlockResource.retrieve<IPageHeaderBlockContent>(
                {
                    bareUrl: headerComponentId,
                    createOnError: true
                },
                undefined
            );

            if (block) {
                const content = block.data?.content as IPageHeaderBlockContent;
                const ids = content?.blockedProducts?.split(',').map(Number) || [];
                this.seasonalyBlockedProductsIds = new Set(ids);
                // remove empty strings from the array
                const groupsString = content?.whitelistedGroups?.split(',')?.filter(Boolean) || [];
                const groups = groupsString.map(Number);
                this.whitelistedGroups = new Set(groups);
            }
            this.seasonalLoaded = true;
        });
    }

    private recheck(): void {
        const meta = this.meta.model;

        // do nothing for seasonal products
        if (meta?.type === Product.type && this.isSeasonalyRestricted(meta as Product)) {
            return;
        }

        // for vip products you may need to display the restricted content modal
        if (this.hasRestrictedContent(meta)) {
            this.displayRestrictedContentModal();
        } else {
            this.restrictedContentRef?.close();
            if (this.canSeeAssetByVipRules(this.meta.model)) {
                this.vipContentModalRef?.close();
            } else {
                this.displayModal();
            }
        }
    }

    private hasRestrictedContent(model: Model | undefined): boolean {
        if (!model) {
            return false;
        }
        if (model.type === Product.type) {
            return this.restrictedData.has(model as Product);
        }
        if (model.type === Category.type && !(model as Category).products.length) {
            return this.productResource
                .getAll(((model as Category).data as ICategoryDataV2).products || [])
                .some(el => this.restrictedData.has(el));
        }
        return false;
    }

    private canSeeAssetByVipRules(model: Model | undefined): boolean {
        if (!model) {
            return true;
        }
        if (!this.isVipCategory(model) || (this.customer.model.isLoggedIn && this.vipIds?.includes(this.customer.model.data.group_id))) {
            return true;
        }
        return true;
    }

    private isVipCategory(model: Model): boolean {
        return model?.type === Category.type && ((model as Category).data as ICategoryDataV3)?.vip_only && model.data!.verbosity > 2
            ? true
            : false;
    }

    private isRestrictedProduct(data: IProductData): boolean {
        return !!(data.season || data.lm_subcategory) || this.seasonalyBlockedProductsIds.has(data.id);
    }

    private async load(): Promise<void> {
        if (!this.vipIds) {
            await this.app.customer.model.load();
            const config = await this.cmsConfigResource.loadModelByUrl(CMS_CONFIG_VIP);
            this.vipIds = config?.data?.content.vipIds?.split(',').map(el => Number(el)) || [];
        }
    }

    private displayModal = (): void => {
        if (!this.vipContentModalRef) {
            this.dfModalService.open(undefined, NProductVipContentModal.BLOCK_ID, undefined, undefined, el => {
                this.vipContentModalRef = el;
                this.vipContentModalRef?.beforeClosed().subscribe(() => {
                    this.vipContentModalRef = undefined;
                });
            });
        }
    };

    private displayRestrictedContentModal = (): void => {
        if (!this.restrictedContentRef) {
            this.dfModalService.open(undefined, NVipRestrictedContentModal.BLOCK_ID, undefined, undefined, el => {
                this.restrictedContentRef = el;
                this.restrictedContentRef?.beforeClosed().subscribe(() => {
                    this.restrictedContentRef = undefined;
                });
            });
        }
    };

    private applyProductsRestriction(products: Product[]) {
        const source = this.customer.model.data;
        const a = source.allowed_subcategory;
        const b = source.allowed_seasons;

        if (a || b || this.seasonalyBlockedProductsIds.size) {
            products.forEach(product => {
                const data = product.rawData || (this.restrictedData.has(product) && this.restrictedData.get(product));

                if (data) {
                    const pa = data.lm_subcategory;
                    const pb = data.season;

                    if (
                        this.isSeasonalyRestricted(product) ||
                        (a && pa && !pa.some(r => a.includes(r))) ||
                        (b && pb && !pb.some(r => b.includes(r)))
                    ) {
                        this.restrictedData.set(product, data);
                        product.unload();

                        this.sanitizeBasket();
                    } else {
                        if (this.restrictedData.has(product)) {
                            product.setData(this.restrictedData.get(product)!);
                            this.restrictedData.delete(product);
                        }
                    }
                }
            });
        }
    }

    private isSeasonalyRestricted(product: Product): boolean {
        const customerGroupId = this.customer.model.data.group_id;
        if (this.whitelistedGroups.has(customerGroupId)) {
            return false;
        }
        return this.seasonalyBlockedProductsIds.has(product.id);
    }

    private sanitizeBasket = this.timeoutService.debounce(() => {
        const items = this.customer.basket.items;
        if (items.length) {
            const restrictedProducts = Array.from(this.restrictedData.keys()).map(i => i.id);
            const invalidBasketItems = this.customer.basket.items.filter(i => restrictedProducts.includes(i.data.product)).map(i => i.id);
            if (invalidBasketItems.length) {
                this.basketService.remove(undefined, invalidBasketItems);
            }
        }
    }, 100);
}
